import type { CarouselImageProperty } from './ProductImage';
import type { ProductVideo as ProductVideoSchema } from '../../../../amplienceTypes/schemas/imported/product-video-schema';
import type { ReactElement, MouseEvent, Dispatch, SetStateAction, PropsWithChildren } from 'react';

import styled, { css } from 'styled-components';

import { CarouselItem } from './CarouselItem';
import { useCarouselAnimation } from './hooks/useCarouselAnimation';
import { useCarouselEvents } from './hooks/useCarouselEvents';
import { useCarouselProgressBar } from './hooks/useCarouselProgressBar';
import { useCarouselState } from './hooks/useCarouselState';
import { useStyleVariant } from './hooks/useStyleVariant';
import type { ProductImageGallery2 } from '~/amplienceTypes/schemas/imported/product-image-gallery-schema';
import { ProgressBar } from '~/components/shared/common/ProgressBar';

export interface ProductImageGalleryHybrisProps {
  productVideo: {
    content: ProductVideoSchema;
    [k: string]: unknown;
  };
}

export type AnimationDirection = 'left' | 'right';

const S = {
  GridCarousel: styled.div`
    overflow: hidden;
    position: relative;
    height: 100%;
  `,
  GridCarouselInner: styled.ul`
    ${({ theme }) => css`
      list-style: none;
      width: 100%;
      height: ${theme.variables.product.carousel.height};
      position: relative;
      margin: 0;
      padding: 0;

      &:where([data-is-swipe-mode='true'] &) {
        display: flex;
        flex-direction: row;
        scroll-snap-type: x mandatory;
        gap: 1px;
        overflow-x: auto;
        scrollbar-width: none;

        &::-webkit-scrollbar {
          display: none;
        }
      }

      &:where([data-carousel-has-one-image='true'] &) {
        height: auto;
      }
    `}
  `,
  ProgressBar: styled(ProgressBar)`
    position: static;
  `,
};

export interface ProductCarouselVideoViewProps
  extends ProductImageGallery2,
    ProductImageGalleryHybrisProps,
    PropsWithChildren {
  productImageList: CarouselImageProperty[];
  setZoomPanelScrollXCenter: (e: MouseEvent) => void;
  currentImageIndex: number;
  setCurrentImageIndex: Dispatch<SetStateAction<number>>;
}

export const ProductCarouselVideoView = ({
  autoSlide,
  productVideo,
  productImageList,
  setZoomPanelScrollXCenter,
  currentImageIndex,
  setCurrentImageIndex,
  children,
}: ProductCarouselVideoViewProps): ReactElement => {
  // Initialize state management
  const carouselState = useCarouselState({ autoSlide });
  const progressPercentage = ((currentImageIndex + 1) / productImageList.length) * 100;
  const hideProgressBar = productImageList.length === 1;

  // Initialize animation logic
  const { slideToNextImage } = useCarouselAnimation({
    ...carouselState,
    currentImageIndex,
    setCurrentImageIndex,
    productImageList,
  });

  // Initialize event handling
  const { handleTouchEnd } = useCarouselEvents({
    ...carouselState,
    setCurrentImageIndex,
    currentImageIndex,
    productImageList,
    slideToNextImage,
  });

  const { styleVariant } = useStyleVariant();

  useCarouselProgressBar({
    galleryRef: carouselState.galleryRef,
    progressBarRef: carouselState.progressBarRef,
  });

  return (
    <>
      <S.GridCarousel
        data-carousel-has-one-image={productImageList.length === 1}
        data-is-swipe-mode={carouselState.isSwipeMode}
      >
        {children}

        <S.GridCarouselInner
          ref={carouselState.galleryRef}
          onTouchEnd={handleTouchEnd}
          data-testid="image-gallery-list"
        >
          {productImageList?.map((productImage, index) => (
            <CarouselItem
              key={`${productImage.original.url}${index}`}
              productImage={productImage}
              styleVariant={styleVariant}
              index={index}
              currentImageIndex={currentImageIndex}
              isSwipeMode={carouselState.isSwipeMode}
              setZoomPanelScrollXCenter={setZoomPanelScrollXCenter}
              itemRef={el => {
                if (carouselState.itemRefs.current) {
                  carouselState.itemRefs.current[index] = el;
                }
              }}
              video={carouselState.video}
              videoActive={carouselState.videoActive}
              setVideoActive={carouselState.setVideoActive}
              autoplay={carouselState.autoplay}
              productVideoContent={productVideo?.content}
            />
          ))}
        </S.GridCarouselInner>
      </S.GridCarousel>

      {!hideProgressBar && (
        <S.ProgressBar
          ref={carouselState.progressBarRef}
          progress={progressPercentage}
          data-testid="progress-bar"
        />
      )}
    </>
  );
};
